import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getVideoUrl, hasMobileVideo } from '../lib/videoUrls';

interface VideoPlayerProps {
  src: string;
  title: string;
  className?: string;
  loop?: boolean;
  rounded?: boolean;
  muted?: boolean;
  preserveAspectRatio?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, title, className = "", loop = false, rounded = true, muted = false, preserveAspectRatio = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isMuted, setIsMuted] = useState(muted || loop); // Muted by default for loop videos or when explicitly muted
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [hideMobileUI, setHideMobileUI] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Detect iOS for preload strategy
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // Helper function to get the appropriate video source
  const getAppropriateVideoSrc = (isMobileDevice: boolean) => {
    // If src is already a full CloudFront URL, handle it properly
    if (src.includes('cloudfront.net')) {
      const filename = src.split('/').pop() || src;
      
      // Check if mobile version exists and we're on mobile
      if (isMobileDevice && hasMobileVideo(filename)) {
        const mobileUrl = getVideoUrl(filename, true);
        // Ensure we get a valid CloudFront URL
        return mobileUrl.includes('cloudfront.net') ? mobileUrl : src;
      }
      
      // Return original src for desktop or if no mobile version
      return src;
    }
    
    // If src is just a filename, use getVideoUrl
    const videoUrl = getVideoUrl(src, isMobileDevice);
    return videoUrl.includes('cloudfront.net') ? videoUrl : src;
  };

  const [currentVideoSrc, setCurrentVideoSrc] = useState(() => {
    const initialIsMobile = window.innerWidth < 768;
    const filename = src.split('/').pop() || src;
    
    if (src.includes('cloudfront.net') && initialIsMobile && hasMobileVideo(filename)) {
      return getVideoUrl(filename, true);
    }
    return src;
  });

  // Detect mobile breakpoint and update video source with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newIsMobile = window.innerWidth < 768;
        setIsMobile(newIsMobile);
        
        const newVideoSrc = getAppropriateVideoSrc(newIsMobile);
        if (newVideoSrc !== currentVideoSrc) {
          setCurrentVideoSrc(newVideoSrc);
          setIsLoaded(false);
          setIsLoading(true);
          if (videoRef.current) {
            videoRef.current.load();
          }
        }
      }, 150); // Debounce 150ms
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
    };
  }, [src, currentVideoSrc]);

  // Mobile-specific video reset
  useEffect(() => {
    if (isMobile && videoRef.current) {
      // Force iOS to recognize video element
      videoRef.current.load();
    }
  }, [currentVideoSrc, isMobile]);

  // Auto-play effect for loop videos
  useEffect(() => {
    if (loop && videoRef.current) {
      const video = videoRef.current;
      console.log('Setting up autoplay for loop video:', src);
      
      const attemptPlay = () => {
        console.log('Attempting to play video...');
        video.play().then(() => {
          console.log('Video started playing successfully');
          setIsPlaying(true);
        }).catch((error) => {
          console.error('Autoplay failed:', error);
          // Show play button overlay
          setIsPlaying(false);
        });
      };

      // Try multiple approaches
      const tryPlay = () => {
        if (video.readyState >= 2) {
          attemptPlay();
        } else {
          video.addEventListener('canplay', attemptPlay, { once: true });
          video.addEventListener('loadeddata', attemptPlay, { once: true });
        }
      };

      // Try immediately
      tryPlay();
    }
  }, [loop, currentVideoSrc]);

  // Video protection measures
  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    
    if (!container || !video) return;

    // Disable right-click context menu
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
    };

    // Disable drag and drop
    const handleDragStart = (e: Event) => {
      e.preventDefault();
    };

    // Disable text selection
    const handleSelectStart = (e: Event) => {
      e.preventDefault();
    };

    // Add event listeners
    container.addEventListener('contextmenu', handleContextMenu);
    container.addEventListener('dragstart', handleDragStart);
    container.addEventListener('selectstart', handleSelectStart);

    // Disable video controls context menu
    video.addEventListener('contextmenu', handleContextMenu);
    video.addEventListener('dragstart', handleDragStart);

    // Cleanup
    return () => {
      container.removeEventListener('contextmenu', handleContextMenu);
      container.removeEventListener('dragstart', handleDragStart);
      container.removeEventListener('selectstart', handleSelectStart);
      video.removeEventListener('contextmenu', handleContextMenu);
      video.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  const togglePlay = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        // Show UI again when paused on mobile
        if (isMobile) {
          setHideMobileUI(false);
        }
      } else {
        // On mobile, ensure video is loaded before playing
        if (isMobile && videoRef.current.readyState < 2) {
          videoRef.current.load();
          videoRef.current.addEventListener('loadeddata', () => {
            videoRef.current?.play().then(() => {
              setIsPlaying(true);
              if (isMobile) {
                setHideMobileUI(true);
              }
            }).catch((error) => {
              console.error('Failed to play video:', error);
            });
          }, { once: true });
        } else {
          videoRef.current.play().then(() => {
            setIsPlaying(true);
            // Hide UI when playing on mobile
            if (isMobile) {
              setHideMobileUI(true);
            }
          }).catch((error) => {
            console.error('Failed to play video:', error);
          });
        }
      }
    }
  };

  const toggleMute = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const toggleFullscreen = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    console.log('Fullscreen button clicked, isMobile:', isMobile);
    
    if (videoRef.current) {
      const video = videoRef.current;
      
      // Check if already in fullscreen
      if (document.fullscreenElement || 
          (document as any).webkitFullscreenElement || 
          (document as any).mozFullScreenElement || 
          (document as any).msFullscreenElement) {
        console.log('Exiting fullscreen...');
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
      } else {
        console.log('Entering fullscreen...');
        // Enter fullscreen
        if (video.requestFullscreen) {
          video.requestFullscreen();
        } else if ((video as any).webkitRequestFullscreen) {
          (video as any).webkitRequestFullscreen();
        } else if ((video as any).mozRequestFullScreen) {
          (video as any).mozRequestFullScreen();
        } else if ((video as any).msRequestFullscreen) {
          (video as any).msRequestFullscreen();
        }
      }
    }
  };

  const handleLoadedData = () => {
    setIsLoaded(true);
    setIsLoading(false);
    console.log('Video loaded successfully:', src);
    // Auto-play if loop is enabled
    if (loop && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Autoplay failed:', error);
        // Try to play again after a short delay
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(console.error);
          }
        }, 100);
      });
    }
  };

  const handleMouseEnter = () => {
    // Don't show controls for loop videos
    if (!loop) {
      setShowControls(true);
    }
  };

  const handleMouseLeave = () => {
    // Don't show controls for loop videos
    if (!loop) {
      setShowControls(false);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`video-protected relative w-full ${preserveAspectRatio ? '' : (loop ? 'aspect-square' : 'aspect-video')} bg-black ${rounded ? 'rounded-lg' : ''} overflow-hidden group ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitUserDrag: 'none',
        KhtmlUserSelect: 'none'
      } as React.CSSProperties}
    >
      <video
        key={currentVideoSrc}
        ref={videoRef}
        src={currentVideoSrc}
        className={`w-full h-full ${preserveAspectRatio ? 'object-contain' : 'object-cover'}`}
        poster=""
        onLoadedData={handleLoadedData}
        onLoadedMetadata={() => {
          if (isMobile) {
            setIsLoading(false);
            setIsLoaded(true);
          }
        }}
        onProgress={() => {
          // If video is buffering, it's loading
          if (isLoading && videoRef.current?.buffered && videoRef.current.buffered.length > 0) {
            setIsLoading(false);
          }
        }}
        onPlay={() => {
          setIsPlaying(true);
          // Hide UI when playing on mobile
          if (isMobile) {
            setHideMobileUI(true);
          }
        }}
        onPause={() => {
          setIsPlaying(false);
          // Show UI again when paused on mobile
          if (isMobile) {
            setHideMobileUI(false);
          }
        }}
        onClick={(e) => {
          // On mobile, allow clicking video to toggle play/pause
          if (isMobile && isLoaded) {
            e.preventDefault();
            togglePlay(e);
          }
        }}
        onError={(e) => {
          console.error('Video error:', e);
          console.error('Video src:', currentVideoSrc);
          console.error('Retry count:', retryCount);
          console.error('Video error details:', e.currentTarget.error);
          
          // iOS-specific: Try reloading on network errors
          if (retryCount < 3) {
            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.load();
                setRetryCount(prev => prev + 1);
              }
            }, 1000 * (retryCount + 1)); // Exponential backoff
          } else {
            setIsLoading(false);
            setIsLoaded(false);
          }
        }}
        onLoadStart={() => {
          setIsLoading(true);
        }}
        onCanPlay={() => {
          setIsLoading(false);
        }}
        onCanPlayThrough={() => {
          setIsLoading(false);
        }}
        onWaiting={() => {
          setIsLoading(true);
        }}
        onStalled={() => {
          if (videoRef.current && retryCount < 2) {
            videoRef.current.load();
            setRetryCount(prev => prev + 1);
          }
        }}
        onSuspend={() => console.log('Video suspended')}
        preload={isIOS ? "metadata" : "auto"}
        playsInline
        loop={loop}
        autoPlay={loop}
        muted={muted || loop || isMobile}
        disablePictureInPicture
        style={{
          pointerEvents: 'auto',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        } as React.CSSProperties}
      />
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <div className="ml-4 text-white text-sm">
            Loading
          </div>
        </div>
      )}
      
      {/* Error State */}
      {!isLoading && !isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="text-white text-center">
            <div className="text-sm mb-2">Video failed to load</div>
            <button 
              onClick={() => {
                setIsLoading(true);
                setIsLoaded(false);
                setRetryCount(0); // Reset retry count
                if (videoRef.current) {
                  videoRef.current.load();
                }
              }}
              className="px-4 py-2 bg-white bg-opacity-20 rounded text-sm hover:bg-opacity-30 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}
      
      {/* Play/Pause Overlay - Only show when not playing, and not hidden on mobile */}
      {!isPlaying && !isLoading && !(isMobile && hideMobileUI) && (
        <motion.div
          className={`absolute inset-0 flex items-center justify-center cursor-pointer ${loop ? 'bg-black bg-opacity-20' : 'bg-black bg-opacity-30'}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          onClick={togglePlay}
        >
          <motion.div
            className={`${loop ? 'w-24 h-24 bg-white bg-opacity-95' : 'w-20 h-20 bg-white bg-opacity-90'} rounded-full flex items-center justify-center shadow-lg`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg
              width={loop ? "32" : "24"}
              height={loop ? "32" : "24"}
              viewBox="0 0 24 24"
              fill="black"
              className="ml-1"
            >
              <path d="M8 5v14l11-7z"/>
            </svg>
          </motion.div>
          {loop && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
              Click to play loop
            </div>
          )}
        </motion.div>
      )}

      {/* Custom Controls - Show when hovering and video is loaded (not for loop videos), hide on mobile when playing */}
      {showControls && isLoaded && !loop && !(isMobile && hideMobileUI) && (
        <motion.div
          className="video-controls absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              <button
                onClick={togglePlay}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors"
              >
                {isPlaying ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
              <span className="text-sm font-medium truncate">{title}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleMute}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors"
              >
                {isMuted ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
              <button
                onClick={toggleFullscreen}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  toggleFullscreen();
                }}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors touch-manipulation"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
